import ComponentManager from '@/Management/ComponentManager';
import ControlFactory from '@/Factories/ControlFactory';
import MLStringPopupBoxViewModelType from '@/ViewModels/Controls/MLStringPopupBoxViewModel';
import MLStringPopupBoxType from '@/Controls/MLStringPopupBox';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import MultilineTextBoxViewModelType from '@/ViewModels/Controls/MultilineTextBoxViewModel';
import WindowViewModelType from '@/ViewModels/Controls/WindowViewModel';
import {
    FilterInfo,
    IMLStringPopupBoxFactoryConstructorParameter,
    IMLStringPopupBoxViewModelConstructorParameter
} from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';
import LocatorHelper from '@/Utility/LocatorHelper';
import RADEnvironmentProxy from "@/Communication/RADEnvironmentProxy";
import RADApplication from "@/Management/RADApplication";
import DetailViewContext from "@/ViewModels/Contexts/DetailViewContext";

export default class MLStringPopupBoxFactory extends ControlFactory {

    private MLStringPopupBoxViewModel = ComponentManager.getViewModel<typeof MLStringPopupBoxViewModelType>('MLStringPopupBoxViewModel');
    private MLStringPopupBox = ComponentManager.getControl<typeof MLStringPopupBoxType>('MLStringPopupBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private MultilineTextBoxViewModel = ComponentManager.getViewModel<typeof MultilineTextBoxViewModelType>('MultilineTextBoxViewModel');

    private technicalNameRootProperty = '';
    private technicalNameValueMLS = '';
    private technicalNameValueMLSForEach = '';
    private lastValue: string;
    private isValueModified = false;

    /**
    * Das JQuery-Element des Controls
    */
    private jqueryControl: JQuery;

    /**
     * @override
     */
    public viewModel: MLStringPopupBoxViewModelType;

    public multiline: boolean;
    public enablePopUp: boolean;

    public constructor(private params: IMLStringPopupBoxFactoryConstructorParameter) {
        super(params);

        this.multiline = this.paramOrDefault(params.multiline, false);
        this.enablePopUp = this.paramOrDefault(params.enablePopUp, true);
    }

    /**
     * @override
     */
    control: MLStringPopupBoxType;

    public createParamForViewModel(): IMLStringPopupBoxViewModelConstructorParameter {
        this.validateTechName();

        this.context.genericObject.suppressNotificationFor(this.detailViewItem.name);
        this.context.genericObject.suppressNotificationFor(this.technicalNameValueMLSForEach);

        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            length = 255;
        }

        return {
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isLinkFocusable: this.isFocusable,
            isMultilineTextBoxFocusable: this.isFocusable,
            value: this.context.genericObject.getObservable(this.technicalNameValueMLS),
            separatorString: this.context.genericObject.getValue(this.technicalNameRootProperty + '.SeparatorString'),

            //SAGGRC-1499: Beim Zurücksetzen der DerivedOverwritable-Eigenschaft kann sich der Wert von isEachLanguageEqual ändern.
            //Damit dies auch in der Benutzeroberfläche aktualisiert wird, muss hier Observable verwendet werden.
            isEachLanguageEqual: this.context.genericObject.getObservable(this.technicalNameValueMLSForEach),
            ergName: this.detailViewItem.ergName,
            isMultiline: this.multiline,

            maxLength: length,
            parentLocator: this.locator,
            locator: this.technicalNameRootProperty,

            autoTranslation: this.createAutoTranslationParams(),

            multilineTextBox: new this.MultilineTextBoxViewModel({
                height: (this as any).height, // TODO: Typings, bzw. wo kommt dies her?
                isEnabled: this.isEnabled,
                isFocusable: this.isFocusable,
                isValid: this.isValid,
                isMultiline: this.multiline,
                value: this.createValueObservable(),
                invalidationText: this.invalidationText,
                onSubmit: this.params.onSubmit,
                maxLength: length,
                parentLocator: this.locator,
                locator: this.detailViewItem.name,
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                }),
                enablePopUp: this.enablePopUp,
                name: this.detailViewItem.name,
                ergName: this.detailViewItem.ergName,
            })
        };
    }

    private createAutoTranslationParams(): { onRegistration: () => void; onCancellation: () => void; } {
        const interceptorIdentifier = `${this.identifier}_autoTranslation`;
        const isTranslationProviderActive = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTranslationProvider');
        if (!isTranslationProviderActive)
            return null;
        
        return {
            onRegistration: () => {
                // In IEGs ist der Context kein DetailViewContext...
                if (!(this.context instanceof DetailViewContext) || this.context.isDisposedOrDisposing())
                    return;

                if (this.context.hasOnBeforeApplyInterceptor(interceptorIdentifier))
                    return;

                this.context.setOnBeforeApplyInterceptor(interceptorIdentifier, async () => {
                    const registrationResult = await RADEnvironmentProxy.getInstance().registerForAutoTranslation({
                        pdTypeName: this.context.genericObject.pdTypeName,
                        pdoId: this.context.genericObject.pdoId,
                        propertyName: this.detailViewItem.name,
                        originLanguage: RADApplication.getInstance().currentLanguage(),
                    });

                    if (!this.validateStatus(registrationResult, false)) {
                        return {
                            success: false,
                            errorMessage: registrationResult.errorMessage
                        };
                    }

                    return {
                        success: true
                    };
                });
            },
            onCancellation: () => {
                // In IEGs ist der Context kein DetailViewContext...
                if (!(this.context instanceof DetailViewContext) || this.context.isDisposedOrDisposing())
                    return;

                this.context.removeOnBeforeApplyInterceptor(interceptorIdentifier);
            },
        };
    }

    private createValueObservable(): KnockoutObservable<string> {
        if (!this.detailViewItem.isDerivedOverwritable)
            return this.context.genericObject.getObservable<string>(this.detailViewItem.name);

        // MLString Fallback Behandlung: Leerer Text darf bei DerivedOverwritable nicht dazu führen, dass ein Fallback Wert verwendet wird (SAGGRC-1498)
        return this.context.genericObject.getComputed(this.detailViewItem.name, {
            read: (obs: KnockoutObservable<string>) => {
                const value = obs();
                if (this.isValueModified)
                    return this.lastValue;

                // Sonderfall: Textbox wurde mit leerem String initialisiert und der Server liefert durch den Fallback einen Wert
                if (this.lastValue === '')
                    return this.lastValue;

                return value;
            },
            write: (obs: KnockoutObservable<string>, newValue: string) => {
                if (this.lastValue !== undefined)
                    this.isValueModified = true;

                this.lastValue = newValue;

                obs(newValue);
            }
        });
    }

    public createViewModel() {
        const param = this.createParamForViewModel();
        return new this.MLStringPopupBoxViewModel(param);
    }

    public getControl(): JQuery<HTMLElement> {
        if (this.jqueryControl != null) {
            return this.jqueryControl;
        }

        this.viewModel = this.createViewModel();

        this.control = new this.MLStringPopupBox(this.viewModel);

        const tuple = this.control.getTuple();
        this.jqueryControl = tuple.control;

        this.addOverwritableToControl(this.jqueryControl, [this.technicalNameValueMLS], 'REVERT_MLSTRING_BUTTON_TOOLTIP');
        this.registerDefaultValidation([this.technicalNameRootProperty, this.detailViewItem.name, this.technicalNameValueMLS, this.technicalNameValueMLSForEach]);
        this.registerCustomValidation([this.detailViewItem.name], this.viewModel.value, (value: string): boolean => {
            return Utilities.validateInputForInvalidCharacters(value);
        });
        return this.jqueryControl;
    }

    public getAvailableOperators(): Array<{ ergName: string, value: string }> {
        let result = [];

        result.push({ ergName: 'LIKE', value: 'LIKE' });

        return result;
    }

    public getValueForFilter() {
        if (this.viewModel && !this.viewModel.isDisposed) {
            const ret = {};
            ret[this.technicalNameValueMLS] = this.viewModel.multilineTextBox.value();
            return ret;
        }
        return null;
    }

    public getFilterInfo(): FilterInfo {
        return {
            field: this.technicalNameValueMLS,
            value: this.viewModel?.multilineTextBox.value() || ''
        }
    }

    private validateTechName() {
        this.technicalNameRootProperty = this.detailViewItem.name;

        if (this.detailViewItem.name.substring(this.detailViewItem.name.length - 6) === '.Value') {
            this.technicalNameRootProperty = this.detailViewItem.name.substring(0, this.detailViewItem.name.length - 6);
        }

        this.technicalNameValueMLS = this.technicalNameRootProperty + '.ValueMLS';
        this.technicalNameValueMLSForEach = this.technicalNameRootProperty + '.ForEachLanguageEquals';
    }
}

ComponentManager.setFactory('MLStringPopupBox', MLStringPopupBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');